'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { LocaleSwitcher } from './LocaleSwitcher';
import { AnimatePresence, motion } from 'motion/react';
import { localized, type NavigationItemConfig, type RestaurantLocale } from '@restaurant/config';
import styles from './MobileNav.module.css';

const MENU_CATEGORIES = [
  { id: 'menu-du-jour', slug: 'menu-du-jour' },
  { id: 'entrees', slug: 'entrees' },
  { id: 'plats', slug: 'plats' },
  { id: 'desserts', slug: 'desserts' },
  { id: 'boissons', slug: 'boissons' },
] as const;

interface MobileNavProps {
  navigation: NavigationItemConfig[];
  locale: RestaurantLocale;
}

export function MobileNav({ navigation, locale }: MobileNavProps) {
  const t = useTranslations('common');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return undefined;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    const previousHtmlOverflow = documentElement.style.overflow;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      documentElement.style.overflow = previousHtmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [drawerOpen]);

  function close() {
    setDrawerOpen(false);
    setMenuExpanded(false);
  }

  const visibleLinks = navigation.filter((item) => item.isActive && item.showInNav);
  const topLinks = visibleLinks.filter((item) => !['home', 'menu', 'takeaway'].includes(item.key));
  const hasTakeaway = visibleLinks.some((item) => item.key === 'takeaway');

  return (
    <>
      {/* Hamburger toggle */}
      <button
        className={styles.toggle}
        onClick={() => setDrawerOpen((v) => !v)}
        aria-label={drawerOpen ? t('closeMenu') : t('openMenu')}
        aria-expanded={drawerOpen}
      >
        <span className={`${styles.bar} ${drawerOpen ? styles.barTopOpen : ''}`} />
        <span className={`${styles.barMid} ${drawerOpen ? styles.barMidOpen : ''}`} />
        <span className={`${styles.bar} ${drawerOpen ? styles.barBotOpen : ''}`} />
      </button>

      {/* Drawer + backdrop */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className={styles.backdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={close}
              aria-hidden
            />

            {/* Drawer */}
            <motion.nav
              className={styles.drawer}
              aria-label={t('mobileNavigation')}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 340, damping: 36 }}
            >
              {/* Close button */}
              <motion.button
                type="button"
                className={styles.close}
                onClick={close}
                aria-label={t('closeMenu')}
                initial={{ opacity: 0, scale: 0.8, rotate: -90 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.12, type: 'spring', stiffness: 380, damping: 24 }}
              >
                <span className={styles.closeIcon} aria-hidden>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3 3L13 13M13 3L3 13"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </motion.button>

              {/* Scrollable area */}
              <div className={styles.drawerScrollable}>
                {/* Link list */}
                <ul className={styles.navList}>
                  {/* Accueil */}
                  <motion.li
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.03 }}
                  >
                    <Link href="/" className={styles.navLink} onClick={close}>
                      {t('home')}
                    </Link>
                  </motion.li>

                  {/* Menu with accordion */}
                  <motion.li
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <button
                      className={`${styles.linkToggle} ${menuExpanded ? styles.linkToggleOpen : ''}`}
                      onClick={() => setMenuExpanded((v) => !v)}
                      aria-expanded={menuExpanded}
                    >
                      <span>{t('menu')}</span>
                      <span
                        className={`${styles.toggleChevron} ${menuExpanded ? styles.toggleChevronUp : ''}`}
                        aria-hidden
                      >
                        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                          <path
                            d="M2 4L5.5 7.5L9 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>

                    <AnimatePresence>
                      {menuExpanded && (
                        <motion.ul
                          className={styles.subList}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                        >
                          {MENU_CATEGORIES.map(({ id, slug }, i) => (
                            <motion.li
                              key={id}
                              initial={{ opacity: 0, x: 8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.04 + 0.04 }}
                            >
                              <Link
                                href={{ pathname: '/menu/[category]', params: { category: slug } }}
                                className={styles.subLink}
                                onClick={close}
                              >
                                <span className={styles.subDot} aria-hidden />
                                {t(`menuCategories.${id}`)}
                              </Link>
                            </motion.li>
                          ))}
                          <motion.li
                            initial={{ opacity: 0, x: 8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: MENU_CATEGORIES.length * 0.04 + 0.04 }}
                          >
                            <Link
                              href="/menu"
                              className={`${styles.subLink} ${styles.subLinkAll}`}
                              onClick={close}
                            >
                              {t('menuCategories.viewAll')} →
                            </Link>
                          </motion.li>
                          {hasTakeaway && (
                            <motion.li
                              initial={{ opacity: 0, x: 8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: MENU_CATEGORIES.length * 0.04 + 0.08 }}
                            >
                              <Link
                                href="/commande-a-emporter"
                                className={`${styles.subLink} ${styles.subLinkAll}`}
                                onClick={close}
                              >
                                {t('takeawayLong')} →
                              </Link>
                            </motion.li>
                          )}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </motion.li>

                  {/* Other top-level links */}
                  {topLinks.map((item, i) => (
                    <motion.li
                      key={item.key}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (i + 1) * 0.05 + 0.05 }}
                    >
                      <Link href={item.href as any} className={styles.navLink} onClick={close}>
                        {localized(item.labels, locale)}
                      </Link>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA */}
                {hasTakeaway && <motion.div
                  className={styles.ctaBlock}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22 }}
                >
                  <Link href="/commande-a-emporter" className={styles.cta} onClick={close}>
                    {t('order')}
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
                      <path
                        d="M1 6.5h11M6.5 1l5.5 5.5-5.5 5.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </motion.div>}
              </div>

              {/* Footer — outside overflow container so dropdown isn't clipped */}
              <motion.div
                className={styles.drawerFooter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <LocaleSwitcher upward />
              </motion.div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
